import React, { useState, useEffect } from 'react';
import { FiEye, FiMail, FiTrash2, FiSearch } from 'react-icons/fi';
import ConfirmModal from '../components/ConfirmModal';
import { getToken } from '../auth';

const AllFilesView = ({
  fileList,
  parseFilename,
  fieldLabels = [],
  fieldKeys = [],
  sortField,
  sortDirection,
  handleSort,
  API_URL,
  selectedRow,
  setSelectedRow,
  onEmail,
  onDelete
}) => {
  const [filterText, setFilterText] = useState(() => {
    try { return localStorage.getItem('allFiles.filterText') || ''; } catch (_) { return ''; }
  });
  // Persist filter text between sessions
  useEffect(() => {
    try { localStorage.setItem('allFiles.filterText', filterText); } catch (_) {}
  }, [filterText]);

  const [localQueued, setLocalQueued] = useState(() => new Set());
  const [delOpen, setDelOpen] = useState(false);
  const [delName, setDelName] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [zipProg, setZipProg] = useState({ active: false, processed: 0, total: 0 });
  // Pagination: rows per page persisted, current page in-memory
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('allFiles.rowsPerPage') || '50', 10);
      return Number.isFinite(v) && v > 0 ? v : 50;
    } catch (_) { return 50; }
  });
  const [page, setPage] = useState(0);
  const list = Array.isArray(fileList) ? fileList : [];
  const sortedFiles = [...list];

  const statusRank = { error: -1, processing: 0, queued: 1, missing: 2, ready: 3 };
  const getOcrStatus = (f) => {
    if (f.invalid || f.size === 0) return 'error';
    const isLocallyQueued = localQueued.has(f.filename);
    const hasText = !!f.hasText;
    return (f.processing ? 'processing' : ((f.queued || isLocallyQueued || f.forced) ? 'queued' : (hasText ? 'ready' : 'missing')));
  };

  if (sortField) {
    if (sortField === '__ocr__') {
      sortedFiles.sort((a, b) => {
        const sa = statusRank[getOcrStatus(a)] ?? 99;
        const sb = statusRank[getOcrStatus(b)] ?? 99;
        if (sa < sb) return sortDirection === 'asc' ? -1 : 1;
        if (sa > sb) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      sortedFiles.sort((a, b) => {
        const aVal = String(parseFilename(a.filename)[sortField] || '').toLowerCase();
        const bVal = String(parseFilename(b.filename)[sortField] || '').toLowerCase();
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
  }

  const t = filterText.trim().toLowerCase();
  const finalFiles = sortedFiles.filter(f => {
    if (!t) return true;
    const obj = parseFilename(f.filename);
    for (const k of fieldKeys) {
      if (String(obj[k] || '').toLowerCase().includes(t)) return true;
    }
    return false;
  });

  // Persist rows-per-page
  useEffect(() => {
    try { localStorage.setItem('allFiles.rowsPerPage', String(rowsPerPage)); } catch (_) {}
  }, [rowsPerPage]);

  // Clamp/reset page on changes
  const totalPages = Math.max(1, Math.ceil(finalFiles.length / Math.max(1, rowsPerPage)));
  useEffect(() => {
    if (page > totalPages - 1) setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, filterText, sortField, sortDirection, finalFiles.length]);

  const startIdx = page * rowsPerPage;
  const endIdx = Math.min(finalFiles.length, startIdx + rowsPerPage);
  const pageFiles = finalFiles.slice(startIdx, endIdx);

  return (
    <div>
      <div style={titleRow}>
        <h2 style={{ margin: 0, fontSize: 20 }}>All Files</h2>
        <div style={filterWrapper}>
          <FiSearch aria-hidden style={filterIcon} />
          <input
            type="text"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setFilterText(''); }}
            placeholder={`Filter by ${fieldLabels.join(', ')}`}
            style={filterInput}
          />
          {filterText && (
            <button
              type="button"
              onClick={() => setFilterText('')}
              aria-label="Clear filter"
              title="Clear"
              style={filterClear}
            >
              ✕
            </button>
          )}
        </div>
      </div>
      {/* Toolbar between search and table */}
      <div style={toolbarRow}>
        {/* Left: Download button */}
        <div>
          <button
            className="btn btn-primary"
            disabled={selected.size === 0}
            onClick={async () => {
              try {
                const list = Array.from(selected);
                if (!list.length) return;
                const token = (localStorage.getItem('auth_token')||'');
                // Create a progress channel
                const progressId = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now());
                // Immediately show progress UI
                setZipProg({ active: true, processed: 0, total: list.length });
                try { window.spartaToast && window.spartaToast(`Preparing download: 0/${list.length} files`, 'info', 0); } catch(_) {}
                // Subscribe to progress via SSE (token via query since headers aren't supported)
                let es;
                try {
                  es = new EventSource(`${API_URL}/zip-progress/${encodeURIComponent(progressId)}?token=${encodeURIComponent(token)}`);
                  es.onmessage = (ev) => {
                    try {
                      const j = JSON.parse(ev.data || '{}');
                      if (typeof j.processed === 'number' && typeof j.total === 'number') {
                        setZipProg({ active: true, processed: j.processed, total: j.total });
                        try { window.spartaToast && window.spartaToast(`Preparing download: ${Math.min(j.processed, j.total)}/${j.total} files`, 'info', 0); } catch(_) {}
                      }
                      if (j.done) {
                        setZipProg(p => ({ ...p, active: false }));
                        try { es.close(); } catch (_) {}
                      }
                    } catch (_) {}
                  };
                  es.onerror = () => { try { es.close(); } catch (_) {}; };
                } catch (_) {}
                const res = await fetch(`${API_URL}/files/zip?progressId=${encodeURIComponent(progressId)}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                  body: JSON.stringify({ filenames: list })
                });
                if (!res.ok) {
                  const j = await res.json().catch(() => ({}));
                  return alert(j.message || 'Failed to download zip');
                }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const ts = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
                a.download = `files-${ts}.zip`;
                document.body.appendChild(a);
                a.click();
                setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
                setZipProg({ active: false, processed: 0, total: 0 });
                try { window.spartaToast && window.spartaToast(`Download ready: ${list.length} files`, 'success', 3000); } catch(_) {}
              } catch (e) {
                alert('Failed to download zip');
              }
            }}
            title={selected.size === 0 ? 'Select files to enable download' : 'Download selected files as ZIP'}
          >
            Download
          </button>
        </div>
        {/* Center: File count (match footer label and style) */}
        <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
          Total Files: {finalFiles.length}
          {t && list.length !== finalFiles.length && (
            <div style={{ fontWeight: 'normal' }}>
              Showing {finalFiles.length} files out of Total Files {list.length}
            </div>
          )}
          {zipProg.active && (
            <div style={{ fontWeight: 'normal', marginTop: 4 }}>
              Zipping {Math.min(zipProg.processed, zipProg.total)} of {zipProg.total}...
            </div>
          )}
        </div>
        {/* Right: spacer to center the middle cell */}
        <div />
      </div>
      <div style={scrollContainer}>
        <table style={table}>
          <thead style={theadStyle}>
            <tr>
              <th style={{ ...th, ...thNarrow, borderTopLeftRadius: 8, whiteSpace: 'nowrap', width: 1 }}>Actions</th>
              <th
                style={{ ...th, ...thNarrow, whiteSpace: 'nowrap', width: 1, cursor: 'pointer' }}
                onClick={() => handleSort('__ocr__')}
                title="Sort by OCR status"
              >
                OCR{sortField === '__ocr__' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
              </th>
              <th style={{ ...th, ...thNarrow, whiteSpace: 'nowrap', width: 1 }}>
                <input
                  type="checkbox"
                  aria-label="Select all visible files"
                  checked={pageFiles.length > 0 && pageFiles.every(f => selected.has(f.filename))}
                  indeterminate={undefined}
                  onChange={(e) => {
                    const allSelected = pageFiles.every(f => selected.has(f.filename));
                    setSelected(prev => {
                      const next = new Set(prev);
                      if (allSelected) { pageFiles.forEach(f => next.delete(f.filename)); }
                      else { pageFiles.forEach(f => next.add(f.filename)); }
                      return next;
                    });
                  }}
                />
              </th>
              {fieldKeys.map((field, idx, arr) => (
                <th
                  key={field}
                  style={{
                    ...th,
                    cursor: 'pointer',
                    ...(idx === arr.length - 1 ? { borderTopRightRadius: 8 } : {})
                  }}
                  onClick={() => handleSort(field)}
                >
                  {fieldLabels[idx] || field}
                  {sortField === field && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageFiles.map((f, i) => {
              const globalIndex = startIdx + i;
              const parsed = parseFilename(f.filename);
              const isSelected = globalIndex === selectedRow;
              const baseRow = globalIndex % 2 === 0 ? rowEven : rowOdd;
              const rowStyle = isSelected ? { ...baseRow, ...rowSelected } : baseRow;
              return (
                <tr
                  key={globalIndex}
                  style={rowStyle}
                  onClick={() => setSelectedRow(isSelected ? null : globalIndex)}
                >
                  <td style={{ ...tdNarrow, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, alignItems: 'center' }}>
                      <a
                        href={`${API_URL}/files/${f.filename}?token=${encodeURIComponent(getToken())}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-view"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FiEye title="View" />
                      </a>
                      <button
                        onClick={(e) => { e.stopPropagation(); onEmail && onEmail(f.filename); }}
                        className="btn btn-primary"
                      >
                        <FiMail title="Email" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDelName(f.filename); setDelOpen(true); }}
                        className="btn btn-delete"
                        title="Delete file"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                  <td style={{ ...tdNarrow, whiteSpace: 'nowrap', width: 1 }}>
                    {(() => {
                      if (f.invalid || f.size === 0) {
                        return (
                          <span
                            title="File is zero bytes or invalid"
                            style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, background: 'var(--hover-bg)', color: '#b71c1c', fontSize: 12, border: '1px solid #b71c1c33', whiteSpace: 'nowrap', fontWeight: 600 }}
                          >
                            ! Error
                          </span>
                        );
                      }
                      const isLocallyQueued = localQueued.has(f.filename);
                      const hasText = !!f.hasText;
                      // Prioritize processing/queued over hasText so forced re-OCR doesn't show Ready
                      const status = (f.processing ? 'processing' : ((f.queued || isLocallyQueued || f.forced) ? 'queued' : (hasText ? 'ready' : 'missing')));
                      const color = status === 'ready' ? '#2e7d32' : status === 'processing' ? '#1565c0' : status === 'queued' ? '#ff8f00' : '#b71c1c';
                      const label = status === 'ready' ? 'Ready' : status === 'processing' ? 'OCR…' : status === 'queued' ? 'Queued' : 'No Text';
                      const clickable = status === 'missing';
                      const onClick = async (e) => {
                        e.stopPropagation();
                        if (!clickable) return;
                        try {
                          const res = await fetch(`${API_URL}/files/${encodeURIComponent(f.filename)}/ocr`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('auth_token')||''}` },
                            body: JSON.stringify({ force: true })
                          });
                          if (res.ok) {
                            setLocalQueued(prev => new Set(prev).add(f.filename));
                          } else {
                            const j = await res.json().catch(() => ({}));
                            alert(j.message || 'Failed to enqueue OCR');
                          }
                        } catch (e) {
                          alert('Failed to enqueue OCR');
                        }
                      };
                      return (
                        <span
                          onClick={onClick}
                          title={clickable ? 'Click to OCR now' : undefined}
                          style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, background: 'var(--hover-bg)', color, fontSize: 12, border: `1px solid ${color}33`, cursor: clickable ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                        >
                          {label}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ ...tdNarrow, whiteSpace: 'nowrap', width: 1 }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${f.filename}`}
                      checked={selected.has(f.filename)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelected(prev => {
                          const next = new Set(prev);
                          if (checked) next.add(f.filename); else next.delete(f.filename);
                          return next;
                        });
                      }}
                    />
                  </td>
                  {fieldKeys.map((k) => (
                    <td key={k} style={td}>{parsed[k] || ''}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3 + fieldKeys.length} style={tfootStyle}>
                <div style={pagerRow}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label htmlFor="all-rpp" style={{ opacity: 0.85 }}>Rows per page:</label>
                    <select
                      id="all-rpp"
                      value={rowsPerPage}
                      onChange={(e) => { setRowsPerPage(Math.max(1, parseInt(e.target.value || '50', 10) || 50)); setPage(0); }}
                      style={pagerSelect}
                    >
                      {[25, 50, 100, 200].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ fontWeight: 'bold' }}>
                    {finalFiles.length === 0
                      ? 'No files'
                      : `Showing ${startIdx + 1}–${endIdx} of ${finalFiles.length}`}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button className="btn" onClick={() => setPage(0)} disabled={page <= 0}>First</button>
                    <button className="btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page <= 0}>Prev</button>
                    <span style={{ minWidth: 110, textAlign: 'center' }}>Page {Math.min(page + 1, totalPages)} / {totalPages}</span>
                    <button className="btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</button>
                    <button className="btn" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>Last</button>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <ConfirmModal
        open={delOpen}
        title="Delete File"
        message={`Are you sure you want to delete "${delName}"? This removes the PDF and its text.`}
        confirmLabel="Delete"
        onCancel={() => setDelOpen(false)}
        onConfirm={async () => {
          try {
            if (onDelete) {
              await onDelete(delName);
            } else {
              const token = localStorage.getItem('auth_token') || '';
              await fetch(`${API_URL}/files/${encodeURIComponent(delName)}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
            }
          } catch (_) {}
          setDelOpen(false);
        }}
      />
    </div>
  );
};

const scrollContainer = {
  maxHeight: 'calc(100vh - 140px)',
  overflowY: 'auto',
  paddingBottom: '50px',
};

const theadStyle = {
  position: 'sticky',
  top: 0,
  backgroundColor: 'var(--table-head-bg)',
  zIndex: 1,
};

const table = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: 10,
  fontSize: '13px',
};

const th = {
  textAlign: 'left',
  padding: '10px 8px',
  backgroundColor: 'var(--table-head-bg)',
  color: 'var(--text)',
  borderBottom: '2px solid var(--border)',
  fontSize: '13px',
  fontWeight: 'bold'
};

const td = {
  padding: '8px',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'middle',
};

const tfootStyle = {
  padding: '10px',
  textAlign: 'center',
  fontWeight: 'bold',
  backgroundColor: 'var(--table-head-bg)'
};

const pagerRow = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: 12,
};

const pagerSelect = {
  padding: '6px 8px',
  background: 'var(--card-bg)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
};

const rowEven = { backgroundColor: 'var(--row-even-bg)', cursor: 'pointer', transition: 'background-color 120ms ease' };
const rowOdd = { backgroundColor: 'var(--row-odd-bg)', cursor: 'pointer', transition: 'background-color 120ms ease' };
const rowSelected = {
  backgroundColor: 'var(--row-selected-bg, rgba(100,181,246,0.15))',
  boxShadow: 'inset 0 0 0 2px rgba(100,181,246,0.35)',
};

const titleRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};

const toolbarRow = {
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: 10,
  marginTop: 14, // push toolbar (Download + Total) down a bit
  marginBottom: 4,
};

const filterInput = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  padding: '10px 36px 10px 36px',
  borderRadius: 8,
  border: '1.5px solid var(--primary)',
  backgroundColor: 'var(--card-bg)',
  color: 'var(--text)',
  boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
};

const filterWrapper = {
  position: 'relative',
  flex: 1,
  display: 'flex',
};

const filterIcon = {
  position: 'absolute',
  left: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  opacity: 0.7,
  pointerEvents: 'none'
};

const filterClear = {
  position: 'absolute',
  right: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 24,
  height: 24,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--border)',
  background: 'var(--hover-bg)',
  color: 'var(--text)',
  opacity: 0.9,
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
  borderRadius: 12,
  zIndex: 1,
};

const viewButton = {
  padding: '6px 12px',
  backgroundColor: 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-block',
};

// Narrow variants for compact columns (Actions/OCR)
const thNarrow = { padding: '8px 8px' };
const tdNarrow = { padding: '6px 8px', verticalAlign: 'middle' };

export default AllFilesView;
